import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import packageSample from "../../Assets/package-sample.webp";
import OEMLaundrySheets from "../../Assets/private-label-package.webp";
import kleanLeafProducts from "../../Assets/klean-leaf-products.webp";
import blogHero from "../../Assets/blogs/blog-hero.webp";
import manufactureImage from "../../Assets/blogs/manufacture.webp";
import catalogPdf from "../../Assets/klean_leaf_catalog.pdf";
import Seo, { breadcrumbSchema, productSchema, webPageSchema } from "../Seo/Seo";
import ContactModal from "../ContactModal/ContactModal";

const catalogPath = catalogPdf;

const laundrySheetCards = [
  {
    title: "Scented Laundry Detergent Sheets",
    body: "Everyday laundry sheets with fragrance options for private label brands.",
  },
  {
    title: "Fragrance-Free Laundry Detergent Sheets",
    body: "Unscented sheets for sensitive-positioned or minimalist laundry brands.",
  },
  {
    title: "Colored Laundry Detergent Sheets",
    body: "Custom-colored sheets for brands that want a more visual product style.",
  },
  {
    title: "Extra-Enzyme Laundry Detergent Sheets",
    body: "Enzyme-added sheets designed for stronger stain-removal positioning.",
  },
  {
    title: "Soda-Boosted Laundry Detergent Sheets",
    body: "Sheets with added cleaning support for performance-focused formulas.",
  },
  {
    title: "Solid-Enzyme Laundry Detergent Sheets",
    body: "Laundry sheets with visible enzyme particles for premium product positioning.",
  },
];

const privateLabelPoints = [
  "Private label laundry detergent sheets",
  "Formula and fragrance options",
  "Retail package options",
  "Bulk and refill-style programs",
  "Sample support before production",
  "Export support for international buyers",
  "Suitable for brands, retailers, wholesalers, refill shops, and distributors",
];

const homeCareCategories = [
  {
    title: "Laundry Pods & Laundry Tablets",
    body: "Laundry pods, booster pods, 5-in-1 enzyme pods, and detergent tablet formats.",
  },
  {
    title: "Dishwasher Tablets & Dishwasher Pods",
    body: "Dishwasher tablets, pods, powder, and dishwasher sheet options.",
  },
  {
    title: "Dryer Sheets & Laundry Add-Ons",
    body: "Dryer sheets, wool dryer balls, scent boosters, stain removers, and laundry bags.",
  },
  {
    title: "Cleaning Tablets & Specialty Products",
    body: "Toilet bowl, laundry machine, coffee machine, hand soap, and multi-purpose tablets.",
  },
  {
    title: "Powder & Liquid Detergent Options",
    body: "Laundry powder, cleaning powder, dishwasher powder, and bottled liquid detergent.",
  },
];

const processSteps = [
  "Submit your product inquiry",
  "Discuss formula, fragrance, package, and target market",
  "Review available samples",
  "Confirm package type and receive dielines",
  "Submit artwork for package review",
  "Approve product and package samples",
  "Start production and export planning",
];

function Products() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const showContactModal = () => setIsContactModalOpen(true);
  const handleContactCancel = () => setIsContactModalOpen(false);

  return (
    <>
      <Seo
        title="Private Label Laundry Detergent Sheets & Home Care Products Manufacturer | Klean Leaf"
        description="Klean Leaf manufactures private label laundry detergent sheets, laundry pods, dishwasher tablets, dryer sheets, cleaning tablets, and home-care products for brands, retailers, wholesalers, distributors, and refill businesses."
        path="/products"
        schema={[
          productSchema,
          webPageSchema({
            name: "Private Label Laundry Detergent Sheets & Home Care Products Manufacturer | Klean Leaf",
            description: "Klean Leaf manufactures private label laundry detergent sheets, laundry pods, dishwasher tablets, dryer sheets, cleaning tablets, and home-care products for brands, retailers, wholesalers, distributors, and refill businesses.",
            path: "/products",
            primaryImage: packageSample,
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Products", path: "/products" },
          ]),
        ]}
      />

      <main className="products-page">
        <section className="products-hero">
          <Container>
            <Row className="align-items-center">
              <Col lg={7} className="products-hero-copy">
                <p className="products-eyebrow">Private Label Manufacturer</p>
                <h1>Private Label Laundry Sheets Manufacturer</h1>
                <p className="products-subheadline">
                  Klean Leaf helps brands develop private label laundry detergent sheets and related
                  home-care products with formula, packaging, sample, and export support.
                </p>
                <div className="products-actions">
                  <button type="button" className="products-btn products-btn-primary" onClick={showContactModal}>
                    Request a Private Label Quote
                  </button>
                  <a
                    href={catalogPath}
                    className="products-btn products-btn-secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Product Catalog PDF
                  </a>
                  <Link to="/about" className="products-btn products-btn-ghost">
                    Learn About Klean Leaf
                  </Link>
                </div>
              </Col>
              <Col lg={5} className="products-hero-media">
                <img
                  src={packageSample}
                  alt="Private label laundry detergent sheet packaging manufactured by Klean Leaf"
                  loading="eager"
                  decoding="async"
                />
              </Col>
            </Row>
          </Container>
        </section>

        <section className="products-section-block products-core-section">
          <Container>
            <div className="products-section-heading">
              <p className="products-eyebrow">Core Product Line</p>
              <h2>Private Label Laundry Detergent Sheets</h2>
              <p>
                Laundry detergent sheets are Klean Leaf's core product line. We support scented,
                fragrance-free, colored, enzyme-added, soda-boosted, and solid-enzyme sheet options for
                different brand positions and market needs.
              </p>
            </div>
            <Row className="products-card-grid">
              {laundrySheetCards.map((card) => (
                <Col md={6} lg={4} key={card.title}>
                  <article className="products-card">
                    <h3>{card.title}</h3>
                    <p>{card.body}</p>
                  </article>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        <section className="products-section-block products-private-label-section">
          <Container>
            <Row className="align-items-center">
              <Col lg={6}>
                <img
                  src={OEMLaundrySheets}
                  alt="White private label laundry detergent sheets manufactured by Klean Leaf"
                  className="products-feature-image"
                  loading="lazy"
                  decoding="async"
                />
              </Col>
              <Col lg={6}>
                <p className="products-eyebrow">Private Label Support</p>
                <h2>Built for Laundry Brands, Retailers, and Distributors</h2>
                <p>
                  Klean Leaf works with B2B customers who want to launch, expand, or improve a laundry
                  detergent sheet product line. We can support formula discussion, fragrance selection,
                  packaging structure, dieline preparation, sample review, production planning, and export
                  coordination.
                </p>
                <p>
                  Whether you are building a new brand, adding a laundry sheet SKU to an existing product
                  range, or sourcing a more competitive manufacturing solution, our team can help you move
                  from product idea to finished retail package.
                </p>
                <ul className="products-check-list">
                  {privateLabelPoints.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                <div className="products-actions products-inline-actions">
                  <button type="button" className="products-btn products-btn-primary" onClick={showContactModal}>
                    Contact Us About Laundry Sheets
                  </button>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="products-section-block products-muted-section beyond-products-section">
          <Container>
            <div className="beyond-products-layout">
              <div className="beyond-products-content">
                <div className="products-section-heading">
                  <p className="products-eyebrow">Beyond Laundry Sheets</p>
                  <h2>More Laundry & Home Care Products We Manufacture</h2>
                  <p>
                    Klean Leaf also supports related laundry, dishwashing, cleaning, and household care
                    products for private label programs.
                  </p>
                  <div className="products-actions beyond-products-actions">
                    <a
                      href={catalogPath}
                      className="products-btn products-btn-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Product Catalog
                    </a>
                    <Link
                      to="/blogs/launch-laundry-detergent-sheets-product-line-klean-leaf"
                      className="products-btn products-btn-secondary"
                    >
                      See Product Launch Workflow
                    </Link>
                  </div>
                </div>
                <div className="beyond-products-cards">
                  {homeCareCategories.map((category) => (
                    <article className="products-card products-category-card" key={category.title}>
                      <h3>{category.title}</h3>
                      <p>{category.body}</p>
                    </article>
                  ))}
                </div>
              </div>
              <div className="beyond-products-image">
                <img
                  src={kleanLeafProducts}
                  alt="Klean Leaf laundry and home care products"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="products-section-block products-export-section">
          <Container>
            <div className="products-export-layout">
              <div className="products-export-copy">
                <div className="products-section-heading">
                  <p className="products-eyebrow">Private Label Manufacturing Support</p>
                  <h2>Established Manufacturing With North American Support</h2>
                  <p>
                    Klean Leaf helps brands, retailers, wholesalers, and distributors develop private label
                    laundry and home-care products, with laundry detergent sheets as our main focus.
                  </p>
                  <p>
                    From formula direction and fragrance options to packaging, samples, production
                    planning, and export coordination, our team supports a smoother path from product idea
                    to retail-ready goods.
                  </p>
                </div>
                <div className="products-actions products-export-actions">
                  <Link
                    to="/blogs/how-to-start-private-label-laundry-detergent-sheets-brand"
                    className="products-btn products-btn-light"
                  >
                    How to Start Your Laundry Sheets Brand
                  </Link>
                  <button type="button" className="products-btn products-btn-outline-light" onClick={showContactModal}>
                    Start Your Private Label Project
                  </button>
                </div>
              </div>
              <div className="products-export-image">
                <img
                  src={manufactureImage}
                  alt="Klean Leaf manufacturing support facility"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="products-section-block products-catalog-section">
          <Container>
            <Row className="align-items-center">
              <Col lg={5}>
                <img
                  src={blogHero}
                  alt="Klean Leaf laundry detergent sheets blog guide"
                  className="products-feature-image"
                  loading="lazy"
                  decoding="async"
                />
              </Col>
              <Col lg={7}>
                <p className="products-eyebrow">Learn More</p>
                <h2>Learn Before You Launch</h2>
                <p>
                  Explore Klean Leaf blog guides for product development, packaging, samples, and launch
                  planning. You can also learn more about our company story and manufacturing support.
                </p>
                <div className="products-actions products-catalog-actions">
                  <Link to="/blog" className="products-btn products-btn-primary">
                    Read Klean Leaf Blog
                  </Link>
                  <button type="button" className="products-btn products-btn-secondary" onClick={showContactModal}>
                    Request Samples
                  </button>
                  <Link to="/about" className="products-btn products-btn-secondary">
                    About Klean Leaf
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="products-section-block products-process-section">
          <Container>
            <div className="products-section-heading">
              <p className="products-eyebrow">How It Works</p>
              <h2>From Product Idea to Private Label Order</h2>
              <p>
                Klean Leaf helps customers move through the private label process step by step. After we
                understand your target product, market, fragrance direction, packaging preference, and
                order plan, we can prepare samples, confirm packaging details, provide dielines, review
                artwork, and move into production after approval.
              </p>
            </div>
            <ol className="products-process-list">
              {processSteps.map((step, index) => (
                <li key={step}>
                  <span>{index + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </Container>
        </section>

        <section className="products-final-cta">
          <Container>
            <h2>Ready to Build Your Private Label Laundry Product Line?</h2>
            <p>
              Start with laundry detergent sheets, or expand into dishwasher tablets, laundry pods, dryer
              sheets, cleaning tablets, and other home-care products. Klean Leaf can help you choose the
              right product direction for your brand and market.
            </p>
            <div className="products-actions">
              <button type="button" className="products-btn products-btn-light" onClick={showContactModal}>
                Contact Klean Leaf
              </button>
              <a
                href={catalogPath}
                className="products-btn products-btn-outline-light"
                target="_blank"
                rel="noopener noreferrer"
              >
                View Catalog PDF
              </a>
            </div>
          </Container>
        </section>
      </main>

      <ContactModal
        isModalOpen={isContactModalOpen}
        handleCancel={handleContactCancel}
        idPrefix="products-page-contact"
      />
    </>
  );
}

export default Products;
